import axios, { AxiosResponse, HttpStatusCode } from 'axios';
import httpClient from '@/src/lib/api/http/client';
import {
  STorajaErrorResponse,
  TTorajaErrorResponse,
} from '@/src/shared/data/schemas/responses/base';
import { safeParseZodObject } from '@/src/shared/utils/parser';
import {
  mapLoginToLoginRequest,
  mapRegistrationToRegistrationRequest,
  mapVerifyOTPToVerifyOTPRequest,
} from '../data/mapper';
import { TChangePass } from '../data/schemas/change-pass';
import { TForgotPass } from '../data/schemas/forgot-pass';
import { TLogin } from '../data/schemas/login';
import { TRegistration } from '../data/schemas/registration';
import {
  SForgptPassResponse,
  SLoginResponse,
  SRegisterResponse,
  SVerifyOTPResponse,
  TForgotPassResponse,
  TLoginResponse,
  TRegisterResponse,
  TVerifyOTPResponse,
} from '../data/schemas/response';
import { TVerifyOTP } from '../data/schemas/verify-otp';
import {
  DestroySessionTokenFailedError,
  ForgotPassEmailError,
  ForgotPassFailedError,
  GenerateSessionTokenFailedError,
  InvalidCredentialsError,
  InvalidOTPError,
  LoginFailedError,
  OTPExpiredError,
  RegistrationFailedError,
  ResendOTPFailedError,
  UsernameEmailPhoneNumberAlreadyExistError,
  VerifyOTPFailedError,
} from '../errors';
import { useForgotPassStore } from '../stores/use-forgot-pass-store';
import { tokenService } from './token';

interface ILoginParams {
  input: TLogin;
}

interface IRegisterParams {
  input: TRegistration;
}

interface IVerifyOTPParams {
  input: TVerifyOTP;
}

interface IResendOTPParams {
  input: TRegistration | TForgotPass;
}

interface IForgotPassParams {
  input: TForgotPass;
}

interface IChangePassParams {
  input: TChangePass;
}

export const authService = {
  login: async ({ input }: ILoginParams) => {
    const params = { origin: 'marketplace' };
    const request = await mapLoginToLoginRequest(input);

    try {
      const response: AxiosResponse<TLoginResponse> =
        await httpClient.izmoAuth.post(
          '/authentication/global-login',
          request,
          { params }
        );

      return safeParseZodObject<TLoginResponse>(
        SLoginResponse,
        response.data as TLoginResponse
      );
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (
          error.status &&
          [HttpStatusCode.BadRequest, HttpStatusCode.Unauthorized].includes(
            error.status
          )
        ) {
          throw new InvalidCredentialsError();
        }
        throw new LoginFailedError();
      }

      throw error;
    }
  },

  register: async ({ input }: IRegisterParams) => {
    const request = await mapRegistrationToRegistrationRequest(input);
    const params = { tipe: 'individual', origin: 'marketplace' };

    try {
      const response: AxiosResponse<TRegisterResponse | TTorajaErrorResponse> =
        await httpClient.izmoAuth.post('/authentication/register', request, {
          params,
        });

      return safeParseZodObject<TRegisterResponse>(
        SRegisterResponse,
        response.data as TRegisterResponse
      );
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new RegistrationFailedError();
        }

        const responseData = safeParseZodObject<TTorajaErrorResponse>(
          STorajaErrorResponse,
          error.response.data as TTorajaErrorResponse
        );

        if (
          typeof responseData.error === 'object' &&
          responseData.error.message ===
            'Username/ Email/ No Whatsapp yang dimasukkan sudah digunakkan.'
        ) {
          throw new UsernameEmailPhoneNumberAlreadyExistError();
        }
      }

      throw error;
    }
  },

  verifyOTP: async ({ input }: IVerifyOTPParams) => {
    const request = mapVerifyOTPToVerifyOTPRequest(input);
    const { forgotPassData } = useForgotPassStore.getState();

    const apiPath = forgotPassData
      ? '/authentication/password-reset-verify-otp'
      : '/authentication/register-verify-otp';
    const params = {
      ...(!forgotPassData && { origin: 'marketplace' }),
    };

    try {
      const response: AxiosResponse<TVerifyOTPResponse | TTorajaErrorResponse> =
        await httpClient.izmoAuth.post(apiPath, request, { params });

      return safeParseZodObject<TVerifyOTPResponse>(
        SVerifyOTPResponse,
        response.data as TVerifyOTPResponse
      );
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new VerifyOTPFailedError();
        }

        const responseData = safeParseZodObject<TTorajaErrorResponse>(
          STorajaErrorResponse,
          error.response.data as TTorajaErrorResponse
        );

        if (typeof responseData.error === 'string') {
          switch (responseData.error) {
            case 'Username/Email/No Whatsapp yang dimasukkan sudah digunakan':
              throw new UsernameEmailPhoneNumberAlreadyExistError();
          }
          return;
        }

        switch (responseData.error.title) {
          case 'Kode OTP Expired':
            throw new OTPExpiredError();
          case 'Kode OTP Salah':
            throw new InvalidOTPError();
        }
      }

      throw error;
    }
  },

  resendOTP: async ({ input }: IResendOTPParams) => {
    const request = await mapRegistrationToRegistrationRequest(input);
    const params = {
      resend_otp: true,
    };
    const apiPath =
      'is_business' in input
        ? '/authentication/password-reset'
        : '/authentication/register-verify-otp';

    try {
      await httpClient.izmoAuth.post(apiPath, request, { params });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new ResendOTPFailedError();
      }

      throw error;
    }
  },

  forgotPass: async ({ input }: IForgotPassParams) => {
    const request = {
      gmail: input.email,
      is_business: input.is_business,
    };

    try {
      const response: AxiosResponse<
        TForgotPassResponse | TTorajaErrorResponse
      > = await httpClient.izmoAuth.post(
        '/authentication/password-reset',
        request
      );

      return safeParseZodObject<TForgotPassResponse>(
        SForgptPassResponse,
        response.data as TForgotPassResponse
      );
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (!error.response) {
          throw new ForgotPassFailedError();
        }

        throw new ForgotPassEmailError();
      }

      throw error;
    }
  },

  changePass: async ({ input }: IChangePassParams) => {
    const request = {
      pass: input.password,
      password: input.confirmPassword,
    };

    try {
      const userToken = tokenService.getUserToken();
      const secretToken = tokenService.getSecretToken();

      if (userToken === null && secretToken === null) {
        throw new ForgotPassFailedError();
      }

      const apiPath = `/authentication/password-reset/${userToken}/${secretToken}`;
      await httpClient.izmoAuth.post(apiPath, request);
    } catch (error) {
      if (axios.isAxiosError(error) && !error.response) {
        throw new ForgotPassFailedError();
      }

      throw error;
    }
  },

  generateSessionToken: async () => {
    try {
      await httpClient.izmoAuth.post('/authentication/session', null, {
        headers: {
          Authorization: `Bearer ${tokenService.getRefreshToken()}`,
        },
        withCredentials: true,
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new GenerateSessionTokenFailedError();
      }
      throw error;
    }
  },

  destroySessionToken: async () => {
    try {
      await httpClient.izmoAuth.post('/authentication/session/destroy', null, {
        headers: {
          Authorization: `Bearer ${tokenService.getRefreshToken()}`,
        },
        withCredentials: true,
      });
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw new DestroySessionTokenFailedError();
      }
      throw error;
    }
  },
};
